"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransformAggregateError = exports.EnumArg = exports.OptionalArg = void 0;
exports.maybeApplyProps = maybeApplyProps;
exports.transformXmlArgs = transformXmlArgs;
exports.parseTransformer = parseTransformer;
exports.mergeDfn = mergeDfn;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const xmlutil_1 = require("../../common/xmlutil");
class OptionalArg {
    constructor(type) {
        this.type = type;
    }
}
exports.OptionalArg = OptionalArg;
class EnumArg {
    constructor(values) {
        this.values = values;
    }
    static FromNativeEnum(nativeEnum) {
        return new EnumArg(Object.values(nativeEnum));
    }
    FromXml(value) {
        if (!this.values.includes(value)) {
            throw new Error(`Value ${value} is not a valid enum value. Expected one of '${this.values.join(', ')}'`);
        }
        return value;
    }
}
exports.EnumArg = EnumArg;
function maybeApplyProps(target, from) {
    if (from) {
        Object.assign(target, from);
    }
}
class TransformAggregateError extends AggregateError {
    constructor(errors, innerMessage, key) {
        super(errors, `Error transforming key ${key}: ${innerMessage}`);
        this.errors = errors;
        this.innerMessage = innerMessage;
        this.key = key;
        Object.setPrototypeOf(this, TransformAggregateError.prototype);
    }
}
exports.TransformAggregateError = TransformAggregateError;
const KIND_STRING = 1;
const KIND_NUMBER = 2;
const KIND_BOOLEAN = 3;
const KIND_ENUM = 4;
const KIND_CLASS = 5;
const KIND_FN = 6;
const compiledSpecCache = new WeakMap();
function compileEntry(propKey, xmlKey, raw) {
    let spec = raw;
    let isOptional = false;
    if (raw instanceof OptionalArg) {
        isOptional = true;
        spec = raw.type;
    }
    let isArray = false;
    let inner;
    if (Array.isArray(spec)) {
        if (spec.length !== 1) {
            throw new Error(`Array transform spec must have exactly one element`);
        }
        isArray = true;
        inner = spec[0];
    }
    else {
        inner = spec;
    }
    let kind;
    let payload = null;
    if (inner === 'string') {
        kind = KIND_STRING;
    }
    else if (inner === 'number') {
        kind = KIND_NUMBER;
    }
    else if (inner === 'boolean') {
        kind = KIND_BOOLEAN;
    }
    else if (inner instanceof EnumArg) {
        kind = KIND_ENUM;
        payload = inner;
    }
    else if (typeof inner === 'function') {
        if (typeof inner.FromXml === 'function') {
            kind = KIND_CLASS;
            payload = inner;
        }
        else {
            kind = KIND_FN;
            payload = inner;
        }
    }
    else {
        throw new Error(`Unknown transform spec instruction: ${String(inner)}`);
    }
    return { propKey, xmlKey, kind, payload, isOptional, isArray };
}
function compileSpec(transformSpec) {
    const cached = compiledSpecCache.get(transformSpec);
    if (cached !== undefined) {
        return cached;
    }
    const entries = [];
    for (const propKey of Object.keys(transformSpec)) {
        const tuple = transformSpec[propKey];
        entries.push(compileEntry(propKey, tuple[0], tuple[1]));
    }
    compiledSpecCache.set(transformSpec, entries);
    return entries;
}
function applyInstruction(entry, parent, key, opts) {
    const value = parent[key];
    switch (entry.kind) {
        case KIND_STRING:
            if (typeof value !== 'string') {
                throw new Error(`Expected string value for key ${String(key)}, got ${typeof value}`);
            }
            return value;
        case KIND_NUMBER:
            return (0, xmlutil_1.xmlNum)(value);
        case KIND_BOOLEAN:
            return (0, xmlutil_1.xmlBool)(value);
        case KIND_ENUM:
            return entry.payload.FromXml(value);
        case KIND_CLASS:
            return entry.payload.FromXml(value, opts);
        case KIND_FN:
            return entry.payload(parent, key);
        default:
            throw new Error(`Unknown compiled kind ${entry.kind}`);
    }
}
function transformXmlArgs(xmlData, opts, transformSpec, allowExtraKeys = false) {
    const unDefnd = mergeDfn(xmlData);
    const compiled = compileSpec(transformSpec);
    const result = {};
    let consumed = 0;
    for (let i = 0; i < compiled.length; i++) {
        const entry = compiled[i];
        const xmlKey = entry.xmlKey;
        const present = xmlKey in unDefnd;
        const rawValue = present ? unDefnd[xmlKey] : undefined;
        if (!present || rawValue === undefined || rawValue === null) {
            if (present) {
                consumed++;
            }
            if (entry.isOptional) {
                result[entry.propKey] = null;
                continue;
            }
            if (!entry.isArray) {
                console.error('Missing required key', xmlKey, 'in', unDefnd);
            }
            throw new TransformAggregateError([new Error(`Missing required key ${xmlKey}`)], `Missing required key ${xmlKey}`, entry.propKey);
        }
        consumed++;
        try {
            if (entry.isArray) {
                const arr = sigma_engine_shared_1.Utils.toArray(rawValue);
                const len = arr.length;
                const out = new Array(len);
                for (let j = 0; j < len; j++) {
                    const elem = arr[j];
                    if (elem === undefined || elem === null) {
                        throw new Error(`Missing required key ${j}`);
                    }
                    out[j] = applyInstruction(entry, arr, j, opts);
                }
                result[entry.propKey] = out;
            }
            else {
                result[entry.propKey] = applyInstruction(entry, unDefnd, xmlKey, opts);
            }
        }
        catch (e) {
            if (e instanceof TransformAggregateError) {
                throw new TransformAggregateError(e.errors, e.innerMessage, `${entry.propKey}.${e.key}`);
            }
            if (e instanceof Error) {
                throw new TransformAggregateError([e], e.message, entry.propKey);
            }
            throw e;
        }
    }
    if (!allowExtraKeys) {
        let totalKeys = 0;
        for (const _ in unDefnd) {
            totalKeys++;
        }
        if ('_attributes' in unDefnd) {
            totalKeys--;
        }
        if (totalKeys !== consumed) {
            const xmlKeys = new Set(Object.keys(unDefnd));
            xmlKeys.delete('_attributes');
            for (let i = 0; i < compiled.length; i++) {
                xmlKeys.delete(compiled[i].xmlKey);
            }
            console.error('Extra keys in XML data:', Array.from(xmlKeys).join(', '), JSON.stringify(Object.fromEntries(Array.from(Object.entries(unDefnd)).filter(([k]) => xmlKeys.has(k)))));
            throw new Error(`Extra keys in XML data: ${Array.from(xmlKeys).join(', ')}`);
        }
    }
    return result;
}
function parseTransformer(rawTransformer, opts) {
    function transformSpecInstruction(specInstruction) {
        return function singleItemTransformer(xml, key) {
            const isOptional = rawTransformer instanceof OptionalArg;
            if (!(key in xml) || xml[key] === undefined || xml[key] === null) {
                if (isOptional) {
                    return null;
                }
                console.error('Missing required key', String(key), 'in', xml);
                throw new Error(`Missing required key ${String(key)}`);
            }
            if (specInstruction === 'string') {
                if (typeof xml[key] !== 'string') {
                    throw new Error(`Expected string value for key ${String(key)}, got ${typeof xml[key]}`);
                }
                return xml[key];
            }
            if (specInstruction === 'number') {
                return (0, xmlutil_1.xmlNum)(xml[key]);
            }
            if (specInstruction === 'boolean') {
                return (0, xmlutil_1.xmlBool)(xml[key]);
            }
            if (specInstruction instanceof EnumArg) {
                return specInstruction.FromXml(xml[key]);
            }
            if (typeof specInstruction === 'function') {
                if (typeof specInstruction.FromXml === 'function') {
                    return specInstruction.FromXml(xml[key], opts);
                }
                return specInstruction(xml, key);
            }
            throw new Error(`Unknown transform spec instruction: ${specInstruction}`);
        };
    }
    const transformer = (rawTransformer instanceof OptionalArg ?
        rawTransformer.type
        : rawTransformer);
    if (Array.isArray(transformer)) {
        if (transformer.length !== 1) {
            throw new Error(`Array transform spec must have exactly one element`);
        }
        return function transformArray(xml, key) {
            const isOptional = rawTransformer instanceof OptionalArg;
            if (!(key in xml) || xml[key] === undefined) {
                if (isOptional) {
                    return null;
                }
                throw new Error(`Missing required key ${String(key)}`);
            }
            return sigma_engine_shared_1.Utils.toArray(xml[key]).map((v, i, arr) => transformSpecInstruction(transformer[0])(arr, i));
        };
    }
    return transformSpecInstruction(transformer);
}
function mergeDfn(obj) {
    const { Defn, _attributes: _ignoredParentAttrs, ...rest } = obj;
    if (Defn === undefined || Defn === null) {
        return rest;
    }
    if (typeof Defn === 'object') {
        for (const key of Object.keys(Defn)) {
            if (key === '_attributes') {
                continue;
            }
            if (key in rest) {
                throw new Error(`Key ${key} exists in both Defn and top level and would overwrite it`);
            }
        }
        return {
            ...Defn,
            ...rest,
        };
    }
    return obj;
}
//# sourceMappingURL=xmlTransformer.js.map